/**
 * Story detail shape returned by GET /api/stories/:id.
 * Mirrors src/stories/dto/story-detail-response.dto.ts on the backend
 * (cross-repo shape — keep in sync).
 *
 * Fields persisted as Json on the backend (learningQuestions, citations,
 * storyTextPages) arrive as `unknown` here. Narrow at the component
 * boundary via the parser helpers below; blocks that fail the guard
 * render as "empty" (graceful degradation — never crash).
 */

export type StoryAudioStatus =
  | 'pending'
  | 'generating'
  | 'completed'
  | 'failed'
  | 'retrying'
  | 'not_requested';

export type PdfStatus = 'pending' | 'generating' | 'ready' | 'failed' | 'not_requested';

export type StoryType =
  | 'standard'
  | 'schlauberger_sachgeschichte'
  | 'schlauberger_kurz_erklaert'
  | 'series_plan'
  | 'series_episode'
  | 'sb_research'
  | 'sb_factcheck'
  | 'moderation_check';

export type AgeCluster = 'age_0_2' | 'age_3_4' | 'age_5_7';

export interface StoryDetail {
  id: string;
  worldId: string;
  language: string;
  title: string;
  storyText: string;
  storyTextPages: unknown;
  storyType: StoryType;

  customTheme: string | null;
  personalized: boolean;
  targetAgeCluster: AgeCluster | null;

  childId: string | null;
  /**
   * Denormalized first-name of the associated child profile. Backend
   * populates via Prisma include on GET /api/stories/:id (see
   * src/stories/stories.service.ts StoryWithChildName). Null when the
   * story is non-personalized or the child profile has been deleted.
   */
  childName: string | null;
  familyId: string | null;
  seriesId: string | null;
  episodeNumber: number | null;

  audioStatus: StoryAudioStatus;
  audioDurationS: number | null;
  pdfStatus: PdfStatus;

  learningQuestions: unknown;
  wowFact: string | null;
  tryThis: string | null;
  scientificFacts: string | null;
  parentBox: string | null;
  citations: unknown;

  ratingStars: number | null;
  ratingText: string | null;
  ratedAt: string | null;

  isFavorite: boolean;
  createdAt: string;
}

/**
 * Lightweight row returned by GET /api/stories (list endpoint). Mirrors
 * src/stories/dto/story-list-response.dto.ts (cross-repo shape — keep
 * in sync). Added in Batch 7 for the Start-Page "Deine letzten
 * Geschichten" block + re-used by Batch 8 Library.
 *
 * Deliberately omits content + SB-specific fields — a grid / row-list
 * doesn't need story text, pages, citations, etc. Detail view fetches
 * those via `fetchStoryById` when the user taps into a story.
 */
export interface StoryListItem {
  id: string;
  title: string;
  worldId: string;
  childId: string | null;
  childName: string | null;
  audioStatus: StoryAudioStatus;
  pdfStatus: PdfStatus;
  ratingStars: number | null;
  isFavorite: boolean;
  /**
   * Truncated story-body preview for the Library card (Batch 8 —
   * Briefing Ch 3 line 636). First 200 chars of `Story.storyText`
   * with a trailing ellipsis when truncated. Null when the story
   * body is empty.
   */
  previewText: string | null;
  /**
   * Bug #3 Part 2 (2026-05-13): true if a `story_refund`
   * CreditTransaction exists for this story's jobId. Drives the
   * `credit_refunded` StoryStatusPill on the Library card.
   */
  refunded: boolean;
  createdAt: string;
}

/**
 * Batch 8 envelope — wraps `GET /api/stories` response. Mirrors
 * src/stories/dto/story-list-envelope.dto.ts.
 *
 * `total` reflects the count for the SAME filter conditions as
 * `items` (NOT capped by `limit`). Drives infinite-scroll
 * load-more gating + the "{count} Geschichten" subtitle.
 */
export interface StoryListEnvelope {
  items: StoryListItem[];
  total: number;
}

// ── Parser helpers ─────────────────────────────────────────────

/**
 * Narrow an unknown Json value to string[] — returns null when the value
 * is missing, non-array, empty, or contains non-string entries. Callers
 * treat null as "hide the block" (graceful degradation).
 */
export function asStringArray(v: unknown): string[] | null {
  if (!Array.isArray(v) || v.length === 0) return null;
  const all = v.every((entry) => typeof entry === 'string' && entry.trim().length > 0);
  return all ? (v as string[]) : null;
}

/**
 * Narrow an unknown Json value to StoryTextPages (string[] of page blobs).
 * Falls back to null for empty / malformed — caller renders storyText flat.
 */
export function asStoryPages(v: unknown): string[] | null {
  return asStringArray(v);
}

/**
 * Citations are persisted on the backend as a '; '-joined string (see
 * src/story-jobs/processors/story-create-data.ts::normalizeCitations).
 * Defensive here: accept either the string form we write OR a raw array
 * (in case Claude's output was never normalized — e.g., historical rows).
 * Trims and filters empty entries; returns null on any flavor of absent
 * data so the "Quellen" footer hides instead of rendering "Quellen:".
 */
export function asCitationsString(v: unknown): string | null {
  if (typeof v === 'string') {
    const trimmed = v.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (Array.isArray(v)) {
    const entries = v
      .filter((e): e is string => typeof e === 'string')
      .map((e) => e.trim())
      .filter((e) => e.length > 0);
    return entries.length > 0 ? entries.join('; ') : null;
  }
  return null;
}

// ── Status predicates ──────────────────────────────────────────

const IN_TRANSIT_AUDIO: StoryAudioStatus[] = ['pending', 'generating', 'retrying'];
const IN_TRANSIT_PDF: PdfStatus[] = ['pending', 'generating'];

export function isAudioInTransit(s: StoryAudioStatus): boolean {
  return IN_TRANSIT_AUDIO.includes(s);
}

export function isPdfInTransit(s: PdfStatus): boolean {
  return IN_TRANSIT_PDF.includes(s);
}

export function isAssetPollingNeeded(story: StoryDetail): boolean {
  return isAudioInTransit(story.audioStatus) || isPdfInTransit(story.pdfStatus);
}

// ── Signed-URL + ListeningEvent shapes ─────────────────────────

/**
 * Response shape from GET /api/stories/:id/playback and :id/pdf-url.
 * `expiresAt` is an ISO-8601 string (backend-side `Date` serialized).
 *
 * `downloadUrl` is populated by the audio-playback endpoint — a second
 * signed URL that returns with `Content-Disposition: attachment;
 * filename="…"` so `<a href target="_blank">` triggers the browser's
 * native download dialog (including on iOS Safari, where the `download`
 * attribute is ignored). The PDF endpoint omits this field.
 */
export interface PresignedUrl {
  url: string;
  downloadUrl?: string;
  expiresAt: string;
}

/**
 * Subset of the Prisma `ListeningEventType` enum — PWA only dispatches
 * playback-lifecycle events. `replayed`, `shared`, and `rated` exist on
 * the backend enum but are wired from other features (future batches).
 */
export type PwaListeningEventType =
  | 'started'
  | 'paused'
  | 'resumed'
  | 'completed'
  | 'abandoned'
  | 'skipped_forward'
  | 'skipped_backward';

/**
 * POST /api/listening-events body. `deviceType` is always `'pwa'` from
 * this client. Server denormalizes worldSlug / ageCluster / timeOfDay /
 * dayOfWeek from the referenced Story row.
 */
export interface ListeningEventPayload {
  storyId: string;
  childId?: string;
  deviceType: 'pwa';
  eventType: PwaListeningEventType;
  playbackPositionS: number;
  totalDurationS: number;
  sessionId: string;
}
